import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MobileCTA } from '../components/MobileCTA';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { FileText, Shield, AlertCircle, Clock, Truck, MessageSquare } from 'lucide-react';
import { businessConfig } from '../config/business';

export default function Policies() {
  const policies = [
    {
      id: 'whs',
      title: 'Workplace Health & Safety Policy',
      icon: Shield,
      content: {
        purpose: 'This policy establishes the company\'s approach to ensuring safety in all moving and transport operations and ensures compliance with relevant Australian WHS legislation and the Heavy Vehicle National Law (HVNL).',
        sections: [
          {
            title: 'Responsibilities',
            points: [
              'Employer: Provide safety training, PPE, and properly maintained equipment',
              'Workers: Follow safety procedures, report incidents, ensure safe lifting practices'
            ]
          },
          {
            title: 'Safety Procedures',
            points: [
              'Correct manual handling and lifting techniques must be used at all times',
              'Equipment such as dollies, trolleys, and straps must be checked before use',
              'All vehicles must undergo regular inspection and maintenance'
            ]
          },
          {
            title: 'Incident Reporting',
            points: [
              'All incidents, injuries, and near-misses must be recorded in the Risk/Incident Register'
            ]
          },
          {
            title: 'Compliance',
            points: [
              'Thrifty Move operates in accordance with the HVNL and Chain of Responsibility (CoR) obligations'
            ]
          }
        ]
      }
    },
    {
      id: 'drug-alcohol',
      title: 'Drug & Alcohol Policy',
      icon: AlertCircle,
      content: {
        purpose: 'To ensure all employees operate safely, free from the influence of drugs or alcohol while performing work duties.',
        sections: [
          {
            title: 'Policy Statement',
            points: [
              'Thrifty Move has a zero-tolerance policy for alcohol or illicit drugs in the workplace or while operating company vehicles'
            ]
          },
          {
            title: 'Requirements',
            points: [
              'No employee or contractor shall present for work under the influence of drugs or alcohol',
              'Prescription medication that may impair performance must be disclosed to management',
              'Testing may be conducted if impairment is suspected'
            ]
          },
          {
            title: 'Consequences',
            points: [
              'Any breach of this policy will result in disciplinary action, up to and including termination of engagement'
            ]
          }
        ]
      }
    },
    {
      id: 'fatigue',
      title: 'Fatigue Management Policy',
      icon: Clock,
      content: {
        purpose: 'To manage and prevent driver fatigue, ensure all staff are fit for duty and capable of performing safely under HVNL fatigue management requirements.',
        sections: [
          {
            title: 'Policy Statement',
            points: [
              'Fatigue reduces performance and increases risk',
              'Thrifty Move ensures that all drivers and workers have adequate rest before and after shifts'
            ]
          },
          {
            title: 'Guidelines',
            points: [
              'Maximum shift duration: 12 hours',
              'Minimum rest between shifts: 8 hours',
              'No driving or handling heavy items when fatigued',
              'Drivers must take regular breaks',
              'Drivers may refuse work if unsafe due to tiredness'
            ]
          },
          {
            title: 'Responsibilities',
            points: [
              'Drivers must monitor their fatigue levels',
              'Drivers must report if unfit for duty'
            ]
          }
        ]
      }
    },
    {
      id: 'complaints',
      title: 'Customer Complaints Handling Procedure',
      icon: MessageSquare,
      content: {
        purpose: 'To provide a consistent process for managing and resolving customer complaints effectively and fairly.',
        sections: [
          {
            title: 'Procedure',
            points: [
              'Complaints may be submitted by email to info@thrifty-move.com.au',
              'All complaints will be acknowledged within 48 hours',
              'Investigation will be completed within 10 business days',
              'Findings and resolutions (e.g., repair, refund, compensation) will be communicated in writing'
            ]
          },
          {
            title: 'Review',
            points: [
              'All complaints are logged and reviewed to identify areas for improvement'
            ]
          }
        ]
      }
    },
    {
      id: 'equipment',
      title: 'Moving Equipment & Vehicle Capacity',
      icon: Truck,
      content: {
        purpose: 'We select appropriate vehicle capacity based on the size and requirements of each move. Our crews use professional moving equipment to transport household and office items safely.',
        sections: [
          {
            title: 'Vehicle Selection',
            points: [
              'Truck capacity is matched to your move volume and requirements',
              'For larger moves, we arrange the appropriate truck capacity based on your inventory'
            ]
          },
          {
            title: 'Professional Equipment',
            points: [
              'Moving blankets for furniture protection',
              'Trolleys for efficient loading',
              'Straps and tie-downs for secure transport',
              'Dollies for heavy items',
              'Protective materials for fragile items'
            ]
          },
          {
            title: 'Vehicle Compliance',
            points: [
              'All vehicles are serviced regularly',
              'Vehicles comply with HVNL and CoR requirements',
              'Maintenance, defects, and driver licences are tracked in company registers'
            ]
          }
        ]
      }
    }
  ];

  const faqs = [
    {
      question: 'What areas do you service?',
      answer: 'We provide moving services all across Melbourne and surrounding suburbs. Contact us to confirm if we cover your specific area.'
    },
    {
      question: 'How do I get a quote?',
      answer: 'You can get a free quote by clicking the "Get Your Free Quote" button on our homepage, calling us at 1800 233 023, or filling out our contact form.'
    },
    {
      question: 'Do you provide packing materials?',
      answer: 'Yes! We provide moving blankets, straps, and protective materials. We can also help with packing if needed.'
    },
    {
      question: 'Are you insured?',
      answer: 'Yes, Thrifty Move is fully insured and complies with all Australian regulations including HVNL and Chain of Responsibility requirements.'
    },
    {
      question: 'What truck size will I need?',
      answer: 'The right truck depends on the volume of your move. If you complete our inventory-based estimate, we\'ll use your item volume to help determine the appropriate capacity. If you\'re not sure, request a Quick Quote and our team will confirm the right setup with you.'
    },
    {
      question: 'Can you move on weekends?',
      answer: 'Yes, we operate 7 days a week. Contact us to check availability for your preferred date.'
    },
    {
      question: 'How do I make a complaint?',
      answer: 'We take complaints seriously. Please email us at info@thrifty-move.com.au. All complaints are acknowledged within 48 hours and investigated within 10 business days.'
    },
    {
      question: 'Do you move interstate?',
      answer: 'Currently, we focus on local moves within Melbourne and Victoria. Contact us to discuss your specific requirements.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept various payment methods including cash, bank transfer, and card payments. Payment details will be discussed during your quote.'
    },
    {
      question: 'How far in advance should I book?',
      answer: 'We recommend booking at least 1-2 weeks in advance, especially during peak moving season. However, we can often accommodate last-minute bookings.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>FAQ & Policies | Thrifty Move Melbourne</title>
        <meta name="description" content="Thrifty Move policies and frequently asked questions. WHS, insurance, fatigue management, complaints handling, and more." />
        <link rel="canonical" href="https://thrifty-move.com.au/policies" />
      </Helmet>
      
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gray-900 text-white pt-28 md:pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Policies & FAQ</h1>
            <p className="text-xl text-gray-300">
              Our commitment to safety, compliance, and customer satisfaction
            </p>
          </div>
        </div>
      </section>

      {/* Policies Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Our Policies
            </h2>
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-8">
              <p className="text-gray-800 leading-relaxed">
                <strong className="text-gray-900">Thrifty Move operates in full compliance with the Heavy Vehicle National Law (HVNL) and Chain of Responsibility (CoR) requirements.</strong> Our team follows strict safety and operational standards to ensure every move is performed safely and professionally.
              </p>
            </div>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Thrifty Move is committed to operating safely, professionally, and in full compliance with Australian regulations.
            </p>
            
            <div className="grid grid-cols-1 gap-6">
              {policies.map((policy) => {
                const Icon = policy.icon;
                return (
                  <Card key={policy.id} className="border-2 hover:border-yellow-400 transition-all duration-300">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3 text-2xl">
                        <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-6 h-6 text-yellow-600" />
                        </div>
                        {policy.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                          <p className="text-gray-700"><strong>Purpose:</strong> {policy.content.purpose}</p>
                        </div>
                        
                        {policy.content.sections.map((section, idx) => (
                          <div key={idx} className="mt-4">
                            <h4 className="font-semibold text-gray-900 mb-2">{section.title}</h4>
                            <ul className="list-disc list-inside space-y-1 text-gray-700">
                              {section.points.map((point, pointIdx) => (
                                <li key={pointIdx}>{point}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              Frequently Asked Questions
            </h2>
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-8">
              <p className="text-gray-800 leading-relaxed">
                <strong className="text-gray-900">Thrifty Move operates in full compliance with the Heavy Vehicle National Law (HVNL) and Chain of Responsibility (CoR) requirements.</strong> Our team follows strict safety and operational standards to ensure every move is performed safely and professionally.
              </p>
            </div>
            <p className="text-center text-gray-600 mb-12">
              Have a question? Find answers to common queries below.
            </p>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-2 border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-yellow-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-12 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Still Have Questions?</h3>
              <p className="text-gray-700 mb-6">We're here to help! Contact us for any additional information.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:1800233023" className="inline-flex items-center justify-center px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-lg transition-all duration-200">
                  Call 1800 233 023
                </a>
                <a href="mailto:info@thrifty-move.com.au" className="inline-flex items-center justify-center px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-all duration-200">
                  Email Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <MobileCTA />
    </div>
  );
}